/**
 * Kwolly Featured Collection
 *
 * Custom element handling:
 *  – Carousel prev/next navigation
 *  – Scroll-linked progress bar
 *  – Scroll-reveal animations via IntersectionObserver
 *  – Shopify Theme Editor re-init on section:load
 */

(function () {
  'use strict';

  class KwollyFeaturedCollection extends HTMLElement {
    connectedCallback() {
      this._init();

      // Re-initialise when the merchant edits the section in Theme Editor
      this.addEventListener('shopify:section:load', () => this._init());
    }

    disconnectedCallback() {
      this._teardown();
    }

    // ─── Init / teardown ───────────────────────────────────────────

    _init() {
      this._teardown();
      this._setupCarousel();
      this._setupReveal();
    }

    _teardown() {
      if (this._revealObserver) {
        this._revealObserver.disconnect();
        this._revealObserver = null;
      }

      if (this._carouselListeners) {
        this._carouselListeners.forEach(function (entry) {
          entry.el.removeEventListener(entry.type, entry.fn);
        });
        this._carouselListeners = null;
      }
    }

    // ─── Carousel ──────────────────────────────────────────────────

    _setupCarousel() {
      var carousel = this.querySelector('.kwolly-fc__carousel');
      if (!carousel) return;

      var scrollId = carousel.id;
      var prevBtn = this.querySelector('.kwolly-fc__nav-btn--prev[data-controls="' + scrollId + '"]');
      var nextBtn = this.querySelector('.kwolly-fc__nav-btn--next[data-controls="' + scrollId + '"]');
      var progressFill = scrollId ? this.querySelector('#' + scrollId + '-progress') : null;

      var self = this;
      var listeners = [];

      var onScroll = function () {
        self._updateButtons(carousel, prevBtn, nextBtn);
        self._updateProgress(carousel, progressFill);
      };

      // Passive scroll listener
      carousel.addEventListener('scroll', onScroll, { passive: true });
      listeners.push({ el: carousel, type: 'scroll', fn: onScroll });

      if (prevBtn) {
        var onPrev = function () {
          self._scrollBy(carousel, -1);
        };
        prevBtn.addEventListener('click', onPrev);
        listeners.push({ el: prevBtn, type: 'click', fn: onPrev });
      }

      if (nextBtn) {
        var onNext = function () {
          self._scrollBy(carousel, 1);
        };
        nextBtn.addEventListener('click', onNext);
        listeners.push({ el: nextBtn, type: 'click', fn: onNext });
      }

      this._carouselListeners = listeners;

      // Set initial state
      this._updateButtons(carousel, prevBtn, nextBtn);
      this._updateProgress(carousel, progressFill);
    }

    _scrollBy(carousel, direction) {
      var firstItem = carousel.querySelector('.kwolly-fc__carousel-item');
      var itemWidth = firstItem ? firstItem.offsetWidth : 280;
      var gap = this._getGap(carousel);
      carousel.scrollBy({ left: direction * (itemWidth + gap), behavior: 'smooth' });
    }

    _getGap(carousel) {
      try {
        return parseFloat(getComputedStyle(carousel).columnGap) || 0;
      } catch (_) {
        return 0;
      }
    }

    _updateButtons(carousel, prevBtn, nextBtn) {
      if (!prevBtn && !nextBtn) return;
      var scrollLeft = Math.round(carousel.scrollLeft);
      var maxScroll = carousel.scrollWidth - carousel.clientWidth;

      if (prevBtn) prevBtn.disabled = scrollLeft <= 0;
      if (nextBtn) nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }

    _updateProgress(carousel, fill) {
      if (!fill) return;
      var max = carousel.scrollWidth - carousel.clientWidth;
      var ratio = max > 0 ? carousel.scrollLeft / max : 0;
      fill.style.setProperty('--kwolly-fc-progress', (ratio * 100).toFixed(2) + '%');
    }

    // ─── Scroll-reveal ─────────────────────────────────────────────

    _setupReveal() {
      var self = this;

      // Only animate if the user hasn't requested reduced motion
      var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      var items = this.querySelectorAll('.kwolly-fc__grid-item, .kwolly-fc__carousel-item');
      if (!items.length) return;

      if (prefersReduced) {
        // Immediately visible — no animation
        items.forEach(function (el) {
          el.classList.add('is-visible');
        });
        return;
      }

      // Guard: IntersectionObserver not available in very old Safari
      if (!('IntersectionObserver' in window)) {
        items.forEach(function (el) { el.classList.add('is-visible'); });
        return;
      }

      // Mark root as animated so CSS transitions activate
      this.classList.add('kwolly-fc--animated');

      this._revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              self._revealObserver.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.08,
          rootMargin: '0px 0px -32px 0px',
        }
      );

      items.forEach(function (item) {
        self._revealObserver.observe(item);
      });
    }
  }

  // Register only once to survive hot-reloads in Theme Editor
  if (!customElements.get('kwolly-featured-collection')) {
    customElements.define('kwolly-featured-collection', KwollyFeaturedCollection);
  }
})();
